import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Coins,
  Building2,
  Users,
  Settings,
  MessageSquare,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { usePlatformStripeConfig } from '@/hooks/usePlatformConfig';
import { useLastReconciliationRun, useTriggerReconciliation } from '@/hooks/usePayReconciliation';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';

export function AdminDashboard() {
  const { t } = useTranslation();
  const { data: stripeConfig } = usePlatformStripeConfig();
  const { data: lastRun, isLoading: runLoading } = useLastReconciliationRun();
  const triggerReconciliation = useTriggerReconciliation();
  const [manualInvoiceId, setManualInvoiceId] = useState('');
  const [manualClinicId, setManualClinicId] = useState('');
  const [manualResult, setManualResult] = useState<string | null>(null);

  const stripeConfigured = stripeConfig?.has_test_secret_key || stripeConfig?.has_live_secret_key;
  const isLiveMode = stripeConfig?.stripe_mode === 'live';

  const adminCards = [
    {
      title: t('admin.dashboard.stripe_title'),
      description: t('admin.dashboard.stripe_desc'),
      icon: CreditCard,
      to: '/admin/stripe',
      status: stripeConfigured ? (
        <span className="flex items-center gap-1 text-xs text-ds-green">
          <CheckCircle className="h-3 w-3" />
          {isLiveMode ? t('admin.dashboard.live_mode') : t('admin.dashboard.test_mode')}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-xs text-ds-amber">
          <AlertTriangle className="h-3 w-3" />
          {t('admin.dashboard.not_configured')}
        </span>
      ),
      color: 'bg-ds-teal-tint text-ds-teal',
    },
    {
      title: t('admin.dashboard.billing_title'),
      description: t('admin.dashboard.billing_desc'),
      icon: Coins,
      to: '/admin/billing',
      color: 'bg-ds-amber-tint text-ds-amber',
    },
    {
      title: t('admin.dashboard.clinics_title'),
      description: t('admin.dashboard.clinics_desc'),
      icon: Building2,
      to: '/admin/clinics',
      color: 'bg-ds-teal-tint text-ds-teal',
    },
    {
      title: t('admin.dashboard.sms_title'),
      description: t('admin.dashboard.sms_desc'),
      icon: MessageSquare,
      to: '/admin/sms',
      color: 'bg-ds-teal-tint text-ds-teal',
    },
    {
      title: t('admin.dashboard.users_title'),
      description: t('admin.dashboard.users_desc'),
      icon: Users,
      to: '/admin/users',
      color: 'bg-ds-green-tint text-ds-green',
    },
    {
      title: t('admin.dashboard.settings_title'),
      description: t('admin.dashboard.settings_desc'),
      icon: Settings,
      to: '/admin/settings',
      color: 'bg-ds-neutral-tint text-ds-muted',
    },
  ];

  return (
    <PageLayout title={t('admin.dashboard.title')} subtitle={t('admin.dashboard.subtitle')}>

      {/* Quick Actions Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
        {adminCards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="group relative rounded-xl border border-ds-line bg-ds-card p-6 shadow-card transition hover:border-ds-field hover:shadow-card"
          >
            <div className="flex items-start justify-between">
              <div className={`rounded-lg p-3 ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
              {card.status && <div>{card.status}</div>}
            </div>
            <h3 className="mt-4 text-lg font-semibold text-ds-text">{card.title}</h3>
            <p className="mt-1 text-sm text-ds-muted">{card.description}</p>
            <div className="mt-4 flex items-center text-sm font-medium text-ds-teal">
              {t('admin.dashboard.manage')}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>

      {/* System Status */}
      <div className="mt-8 rounded-xl border border-ds-line bg-ds-card p-6">
        <h2 className="text-lg font-semibold text-ds-text">{t('admin.dashboard.system_status')}</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg bg-ds-sunk p-4">
            <p className="text-sm text-ds-muted">{t('admin.dashboard.stripe_status')}</p>
            <p className="mt-1 text-lg font-semibold text-ds-text">
              {stripeConfigured ? (
                <span className="text-ds-green">
                  {isLiveMode ? t('admin.stripe.live') : t('admin.stripe.test')}
                </span>
              ) : (
                <span className="text-ds-amber">{t('admin.dashboard.not_configured')}</span>
              )}
            </p>
          </div>
          <div className="rounded-lg bg-ds-sunk p-4">
            <p className="text-sm text-ds-muted">{t('admin.dashboard.platform')}</p>
            <p className="mt-1 text-lg font-semibold text-ds-text">Clinio v2.0</p>
          </div>
          <div className="rounded-lg bg-ds-sunk p-4">
            <p className="text-sm text-ds-muted">{t('admin.dashboard.database')}</p>
            <p className="mt-1 text-lg font-semibold text-ds-green">
              {t('admin.dashboard.connected')}
            </p>
          </div>
        </div>
      </div>

      {/* Pay Reconciliation (PAY-11) */}
      <div className="mt-8 rounded-xl border border-ds-line bg-ds-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ds-text">Pay Reconciliation</h2>
            <p className="mt-0.5 text-sm text-ds-muted">
              Daily cron (03:00 UTC) — Stripe ↔ Pay ↔ v2 consistency
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setManualResult(null);
              triggerReconciliation.mutate(
                {},
                {
                  onSuccess: (res) => setManualResult(`Run started: ${res?.data?.run_id ?? '—'}`),
                  onError: (err) =>
                    setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                },
              );
            }}
            disabled={triggerReconciliation.isPending}
            className="shrink-0"
          >
            {triggerReconciliation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run Now
          </Button>
        </div>

        {/* Last run summary */}
        <div className="mt-4">
          {runLoading ? (
            <div className="flex items-center gap-2 text-sm text-ds-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading last run…
            </div>
          ) : lastRun ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Pass 1 */}
              <div className="rounded-lg bg-ds-sunk p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-ds-muted">
                  Pass 1 — Invoice recovery
                </p>
                {lastRun.pass1_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-ds-text">
                    <p>
                      Examined:{' '}
                      <span className="font-semibold">{lastRun.pass1_diffs.examined ?? 0}</span>
                    </p>
                    <p>
                      Fixed:{' '}
                      <span className="font-semibold text-ds-green">
                        {lastRun.pass1_diffs.fixed ?? 0}
                      </span>
                    </p>
                    <p>
                      Expired:{' '}
                      <span className="font-semibold text-ds-amber">
                        {lastRun.pass1_diffs.expired ?? 0}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ds-muted">Not run</p>
                )}
              </div>

              {/* Pass 2 */}
              <div className="rounded-lg bg-ds-sunk p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-ds-muted">
                  Pass 2 — Refund check
                </p>
                {lastRun.pass2_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-ds-text">
                    <p>
                      Examined:{' '}
                      <span className="font-semibold">{lastRun.pass2_diffs.examined ?? 0}</span>
                    </p>
                    <p>
                      Matched:{' '}
                      <span className="font-semibold text-ds-green">
                        {lastRun.pass2_diffs.matched ?? 0}
                      </span>
                    </p>
                    <p>
                      Drift:{' '}
                      <span
                        className={`font-semibold ${(lastRun.pass2_diffs.drift ?? 0) > 0 ? 'text-ds-red' : 'text-ds-text'}`}
                      >
                        {lastRun.pass2_diffs.drift ?? 0}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ds-muted">Not run</p>
                )}
              </div>

              {/* Pass 3 */}
              <div className="rounded-lg bg-ds-sunk p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-ds-muted">
                  Pass 3 — Tenant status
                </p>
                {lastRun.pass3_diffs ? (
                  <div className="mt-1 space-y-0.5 text-sm text-ds-text">
                    <p>
                      Examined:{' '}
                      <span className="font-semibold">{lastRun.pass3_diffs.examined ?? 0}</span>
                    </p>
                    <p>
                      Updated:{' '}
                      <span className="font-semibold">{lastRun.pass3_diffs.updated ?? 0}</span>
                    </p>
                    <p>
                      Restricted:{' '}
                      <span
                        className={`font-semibold ${(lastRun.pass3_diffs.restricted ?? 0) > 0 ? 'text-ds-red' : 'text-ds-text'}`}
                      >
                        {lastRun.pass3_diffs.restricted ?? 0}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ds-muted">Not run</p>
                )}
              </div>

              {/* Pass 4 */}
              <div className="rounded-lg bg-ds-sunk p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-ds-muted">
                  Pass 4 — 7-day drift
                </p>
                {lastRun.pass4_diff ? (
                  <div className="mt-1 space-y-0.5 text-sm text-ds-text">
                    <p>
                      v2 paid:{' '}
                      <span className="font-semibold">{lastRun.pass4_diff.v2_count ?? 0}</span>
                    </p>
                    <p>
                      Pay count:{' '}
                      <span className="font-semibold">{lastRun.pass4_diff.pay_count ?? 0}</span>
                    </p>
                    <p>
                      Diff:{' '}
                      <span
                        className={`font-semibold ${(lastRun.pass4_diff.diff ?? 0) > 0 ? 'text-ds-red' : 'text-ds-green'}`}
                      >
                        {lastRun.pass4_diff.diff ?? 0}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ds-muted">Not run</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-ds-muted">No reconciliation runs yet.</p>
          )}

          {lastRun && (
            <p className="mt-3 text-xs text-ds-muted">
              Last run: {new Date(lastRun.started_at).toLocaleString()}
              {lastRun.finished_at
                ? ` — finished ${new Date(lastRun.finished_at).toLocaleString()}`
                : ' (in progress)'}
              {lastRun.errors_jsonb.length > 0 && (
                <span className="ml-2 text-ds-amber">
                  ⚠ {lastRun.errors_jsonb.length} error(s)
                </span>
              )}
            </p>
          )}
        </div>

        {/* Manual tools */}
        <div className="mt-5 border-t border-ds-line pt-5">
          <p className="mb-3 text-sm font-medium text-ds-text">Manual reconciliation tools</p>
          <div className="flex flex-wrap gap-3">
            {/* Sync from Pay — per invoice */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={manualInvoiceId}
                onChange={(e) => setManualInvoiceId(e.target.value)}
                placeholder="Invoice UUID"
                className="ds-field w-64"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!manualInvoiceId.trim()) return;
                  setManualResult(null);
                  triggerReconciliation.mutate(
                    { invoice_id: manualInvoiceId.trim() },
                    {
                      onSuccess: (res) => {
                        setManualResult(`Sync started: ${res?.data?.run_id ?? '—'}`);
                        setManualInvoiceId('');
                      },
                      onError: (err) =>
                        setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                    },
                  );
                }}
                disabled={triggerReconciliation.isPending || !manualInvoiceId.trim()}
              >
                Sync from Pay
              </Button>
            </div>

            {/* Refresh Pay status — per clinic */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={manualClinicId}
                onChange={(e) => setManualClinicId(e.target.value)}
                placeholder="Clinic UUID"
                className="ds-field w-64"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!manualClinicId.trim()) return;
                  setManualResult(null);
                  triggerReconciliation.mutate(
                    { clinic_id: manualClinicId.trim() },
                    {
                      onSuccess: (res) => {
                        setManualResult(`Refresh started: ${res?.data?.run_id ?? '—'}`);
                        setManualClinicId('');
                      },
                      onError: (err) =>
                        setManualResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`),
                    },
                  );
                }}
                disabled={triggerReconciliation.isPending || !manualClinicId.trim()}
              >
                Refresh Pay status
              </Button>
            </div>
          </div>

          {manualResult && <p className="mt-2 text-xs text-ds-muted">{manualResult}</p>}
        </div>
      </div>
    </PageLayout>
  );
}
